import type { ReactNode } from 'react'
import type { Appointment, AppointmentStatus } from '@/types/clinic'

import { toast } from 'sonner'
import { ChevronDown } from 'lucide-react'
import { useClinic } from '@/lib/clinic/store'
import { useAppDialogs } from '@/components/dialogs/app-dialogs'
import { AppointmentStatusBadge } from '@/components/app/status-badge'

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuLabel,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'

import { APPOINTMENT_STATUS_META } from '@/constants/clinic'

interface AppointmentStatusMenuProps {
    appointment: Appointment
}

// The status badge doubles as a quick menu to move a booking along its workflow.
const AppointmentStatusMenu = ({ appointment }: AppointmentStatusMenuProps): ReactNode => {
    const { data, actions } = useClinic()
    const { openVisit } = useAppDialogs()
    const enabled = data.settings.appointments.enabledStatuses
    const statuses = enabled.includes(appointment.status) ? enabled : [...enabled, appointment.status]
    const patient = data.patients.find(item => item.id === appointment.patientId)

    const handleChange = (value: AppointmentStatus) => {
        if (value === appointment.status) return
        actions.setAppointmentStatus(appointment.id, value)
        const hasVisit = data.visits.some(item => item.appointmentId === appointment.id)
        if (value === 'completed' && !hasVisit) {
            toast.success(`${patient?.name ?? 'Appointment'} marked as completed`, {
                action: { label: 'Record visit', onClick: () => openVisit({ patientId: appointment.patientId, appointmentId: appointment.id }) }
            })
        }
        else {
            toast.success(`Status changed to ${APPOINTMENT_STATUS_META[value].label}`)
        }
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                render={(
                    <button
                        type='button'
                        className='group inline-flex items-center gap-0.5 rounded-full focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none'
                        aria-label={`Status: ${APPOINTMENT_STATUS_META[appointment.status].label}. Change status`}
                    />
                )}
            >
                <AppointmentStatusBadge status={appointment.status} className='transition-[filter] group-hover:brightness-95' />
                <ChevronDown className='size-3.5 text-muted-foreground' aria-hidden='true' />
            </DropdownMenuTrigger>
            <DropdownMenuContent align='start' className='w-48'>
                <DropdownMenuGroup>
                    <DropdownMenuLabel>Update status</DropdownMenuLabel>
                    <DropdownMenuRadioGroup value={appointment.status} onValueChange={value => handleChange(value as AppointmentStatus)}>
                        {statuses.map(status => (
                            <DropdownMenuRadioItem key={status} value={status} closeOnClick>
                                {APPOINTMENT_STATUS_META[status].label}
                            </DropdownMenuRadioItem>
                        ))}
                    </DropdownMenuRadioGroup>
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

export default AppointmentStatusMenu
